/**
 * FGR course classification badge.
 *
 * Visual: outline pill, bold uppercase text (prefix + level rendered as one
 * unit), subtle inner highlight + drop shadow for a tactile "stamp" feel.
 * Uses `var(--primary)` so the brand color is the single source of truth.
 *
 * Variants:
 * - `variant="light"` — force light palette
 * - `variant="dark"`  — force dark palette
 * - omitted           — follow the app's theme (`.dark` class)
 */

import { cn } from '#app/utils/misc.tsx'

export const CLASSIFICATIONS = [
	'AAA1', 'AAA2', 'AAA3',
	'AA1', 'AA2', 'AA3',
	'A1', 'A2', 'A3',
	'BB1', 'BB2', 'BB3',
	'B1', 'B2', 'B3',
	'C1', 'C2', 'C3',
	'D1', 'D2', 'D3',
] as const

export type Classification = (typeof CLASSIFICATIONS)[number]

export function isClassification(value: unknown): value is Classification {
	return (
		typeof value === 'string' &&
		(CLASSIFICATIONS as readonly string[]).includes(value)
	)
}

type Size = 'sm' | 'md' | 'lg'
type Variant = 'light' | 'dark'

type Props = {
	value?: string | null
	size?: Size
	variant?: Variant
	className?: string
}

// Explicit-variant palette. Auto uses theme tokens via Tailwind.
const PALETTE = {
	light: { bg: '#ffffff', accent: '#0075b5' },
	dark: { bg: '#0b1220', accent: '#3fb6e6' },
} as const

const SIZE_TOKENS: Record<
	Size,
	{ height: number; paddingX: number; radius: number; borderWidth: number; fontSize: number }
> = {
	sm: { height: 20, paddingX: 5, radius: 4, borderWidth: 1.5, fontSize: 13 },
	md: { height: 28, paddingX: 7, radius: 6, borderWidth: 2, fontSize: 17 },
	lg: { height: 40, paddingX: 10, radius: 8, borderWidth: 2, fontSize: 26 },
}

const baseClass =
	'inline-flex items-center justify-center select-none align-middle ' +
	'font-display font-bold uppercase antialiased'

export function ClassificationBadge({
	value,
	size = 'md',
	variant,
	className,
}: Props) {
	const normalized = typeof value === 'string' ? value.trim() : value
	if (normalized == null || normalized === '') {
		return <EmptyBadge size={size} className={className} />
	}
	if (!isClassification(normalized)) {
		return <EmptyBadge size={size} className={className} title={String(value)} />
	}

	const t = SIZE_TOKENS[size]
	const palette = variant ? PALETTE[variant] : undefined

	const style: React.CSSProperties = {
		height: t.height,
		paddingInline: t.paddingX,
		borderRadius: t.radius,
		borderWidth: t.borderWidth,
		borderStyle: 'solid',
		fontSize: t.fontSize,
		lineHeight: 1,
		letterSpacing: '0.02em',
		fontFeatureSettings: '"tnum" 1',
		fontVariantNumeric: 'tabular-nums',
		// Inner top highlight + bottom inset + soft outer shadow.
		boxShadow: [
			'inset 0 1px 0 rgba(255,255,255,0.55)',
			'inset 0 -1px 0 rgba(0,0,0,0.04)',
			'0 1px 2px rgba(0,0,0,0.06)',
		].join(', '),
		...(palette
			? {
					backgroundColor: palette.bg,
					color: palette.accent,
					borderColor: palette.accent,
				}
			: null),
	}

	return (
		<span
			role="img"
			aria-label={`Luokitus ${normalized}`}
			className={cn(
				baseClass,
				!palette && 'bg-card text-primary border-primary',
				className,
			)}
			style={style}
		>
			{normalized}
		</span>
	)
}

function EmptyBadge({
	size,
	className,
	title,
}: {
	size: Size
	className?: string
	title?: string
}) {
	const t = SIZE_TOKENS[size]
	return (
		<span
			role="img"
			aria-label="Luokitus puuttuu"
			title={title}
			className={cn(
				baseClass,
				'text-muted-foreground border-muted-foreground/50 bg-transparent',
				className,
			)}
			style={{
				height: t.height,
				paddingInline: t.paddingX,
				borderRadius: t.radius,
				borderWidth: 1,
				borderStyle: 'dashed',
				minWidth: t.height,
				fontSize: t.fontSize,
				lineHeight: 1,
			}}
		>
			–
		</span>
	)
}
